import type { ReactNode } from 'react'

import { t } from '@ctcells/i18n'

import { PrepareModulesDialog, PrepareResult } from '../admin/PrepareModulesRow.tsx'
import { prepareRunGate, usePrepareModulesFlow } from '../admin/prepareModulesFlow.ts'
import { useScreenAccess } from '../screenAccess.ts'
import { ControlButton } from './ModuleControlRows.tsx'

/**
 * 모듈 서랍 「제어」 카드의 **「트레이 자리 정리」**(eric 2026-09-28 「넣어줘」) — `POST /modules/prepare`.
 *
 * 작업을 중지하면 모듈이 트레이 자리(`handoff_occupancy`)를 「차 있음」으로 남기고, 다음 작업의 사전 점검이
 * `HANDOFF_POINT_OCCUPIED_AT_START` 로 막힌다. 모듈 「초기화」는 그 기록을 풀지 않는다. 콘솔이 부를 수 있는
 * 서버 경로는 이것 하나다(`module_prepare.prepare` ③ — 모듈에 묻지 않고 미러가 「차 있음」이라 한 자리만
 * `/handoff/confirm role=pick` 으로 푼다 · 못 읽은 자리는 `human_actions` 로 사람에게 넘긴다 · core `116d330`).
 * ⚠ 종전의 자리 탐침(`/handoff/prepare role=place`)이 모듈을 `TRAY_LOADING` 에 남기던 결함은 걷혔다 —
 *   dev2 실측 2026-10-04: 정리 200 뒤 모듈 넷 `IDLE` · 모듈 상태 원장에 새 전이 0건.
 *
 * 🔴 **관리 › 설정의 「배치 준비 강제 정리」와 같은 연산 · 같은 훅 · 같은 게이트 · 같은 확인 창이다**
 *    (`prepareModulesFlow.ts`). 복붙하지 않는다.
 * 🔴 **종착 안 한 작업이 하나라도 있으면 비활성 + 그 이름**이다 — 서버가 그 작업을 전부 종착시킨다.
 * 🔴 **확인 전 요청 0건.** 확인 창의 「정리 실행」이 보낸다. 창은 누르는 즉시 닫고 결과(`Report`)는
 *    **카드의 「최근 결과」**에 적는다 — 서랍을 보는 사람이 무엇이 풀렸는지 거기서 읽는다.
 * 🔴 **자리 기록은 구독이 따라간다.** 응답으로 `handoff_occupancy` 를 고쳐 쓰지 않는다(낙관적 갱신 금지) —
 *    훅이 모듈 쿼리를 무효화하고, 「점유 중인 자리」 줄(카드)은 서버 값으로만 바뀐다.
 *
 * 「제어」 카드에서는 단추 · 창 · 결과가 카드의 다른 자리에 선다 — `children` 에 셋을 넘긴다.
 * 안 넘기면 셋을 차례로 그린다(단독 시험 · 다른 자리).
 */
export interface ModulePrepareParts {
  /** 조작 단추(위험 톤 · 설명은 툴팁) */
  tile: ReactNode
  /** 확인 창(열렸을 때만) */
  dialog: ReactNode
  /** 결과 — 보내는 중 · 거절 · `Report`. 없으면 `null` */
  result: ReactNode
}

export interface ModulePrepareContainerProps {
  children?: ((parts: ModulePrepareParts) => ReactNode) | undefined
}

export function ModulePrepareContainer({ children }: ModulePrepareContainerProps) {
  const access = useScreenAccess()
  const flow = usePrepareModulesFlow()
  const runGate = prepareRunGate(access, flow.batchesState, {
    preparing: flow.prepare.isPending,
    canSend: true,
  })

  const action = t('action.clearHandoff')
  const tile = (
    <ControlButton
      danger
      description={t('moduleCtl.desc.clearHandoff')}
      gate={runGate}
      icon="list"
      label={action}
      onClick={() => flow.setOpen(true)}
    />
  )
  const result = flow.prepare.isPending ? (
    <p className="small muted" role="status">
      {`${action} — ${t('reason.requestInFlight')}`}
    </p>
  ) : flow.error !== undefined ? (
    <p className="vissue" role="alert">
      {`${action} — ${flow.error}`}
    </p>
  ) : flow.prepare.data !== undefined ? (
    <section aria-label={t('prepare.drawerResult')}>
      <p className="small muted">{t('prepare.drawerResult')}</p>
      <PrepareResult result={flow.prepare.data} />
    </section>
  ) : null
  const dialog = !flow.open ? null : (
    <PrepareModulesDialog
      {...flow.batchesState}
      modulesNotice={flow.modulesNotice}
      onClose={() => flow.setOpen(false)}
      onPrepare={(reason) => {
        flow.setOpen(false)
        flow.send(reason)
      }}
      runGate={runGate}
      targetModules={flow.targetModules}
      title={action}
    />
  )

  if (children !== undefined) return <>{children({ tile, dialog, result })}</>
  return (
    <>
      {tile}
      {result}
      {dialog}
    </>
  )
}
